import React, { useEffect, useMemo, useState } from 'react';
import {
  Search, Plus, Pencil, Trash2, Save, X, Loader2, AlertCircle,
  Package, Tag, Wrench,
} from 'lucide-react';
import MyLayout from './Layout';
import { apiFetchJson } from './api';

const PRODUCTS_ENDPOINT = '/api/admin/products?site=boutique';
const DASHBOARD_ENDPOINT = '/api/admin/sites/boutique/dashboard';
const MAINTENANCE_ENDPOINT = '/api/my/boutique/maintenance';
const DEFAULT_MAINTENANCE = {
  enabled: false,
  message: 'Reservations temporairement indisponibles pendant une maintenance.',
};

const STATUS_CONFIG = {
  available: { label: 'Disponible', cls: 'bg-emerald-500/10 text-emerald-400' },
  reserved: { label: 'Réservé', cls: 'bg-amber-500/10 text-amber-400' },
  sold: { label: 'Vendu', cls: 'bg-gray-500/10 text-gray-400' },
};

const EMPTY_PRODUCT = {
  name: '',
  category: 'pc',
  condition: 'reconditionne',
  grade: 'A',
  price: '',
  description: '',
  processor: '',
  ram: '',
  storage: '',
  screen_size: '',
  warranty: '6 mois',
  stock: 1,
};

function getProductId(product) {
  return product?.id || product?.product_id || '';
}

export default function Boutique() {
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);
  const [maintenance, setMaintenance] = useState(DEFAULT_MAINTENANCE);
  const [maintenanceSaving, setMaintenanceSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError('');

    const [productsResult, dashboardResult, maintenanceResult] = await Promise.allSettled([
      apiFetchJson(PRODUCTS_ENDPOINT, {}, 'Erreur de chargement des produits'),
      apiFetchJson(DASHBOARD_ENDPOINT, {}, 'Erreur de chargement du tableau de bord'),
      apiFetchJson(MAINTENANCE_ENDPOINT, {}, 'Erreur de chargement du mode maintenance'),
    ]);

    if (productsResult.status === 'fulfilled') {
      setProducts(productsResult.value?.products || []);
    } else {
      setProducts([]);
    }

    if (dashboardResult.status === 'fulfilled') {
      setStats(dashboardResult.value);
    } else {
      setStats(null);
    }

    if (maintenanceResult.status === 'fulfilled') {
      setMaintenance({ ...DEFAULT_MAINTENANCE, ...(maintenanceResult.value || {}) });
    } else {
      setMaintenance(DEFAULT_MAINTENANCE);
    }

    const messages = [productsResult, dashboardResult, maintenanceResult]
      .filter(result => result.status === 'rejected')
      .map(result => result.reason?.message)
      .filter(Boolean);

    if (messages.length > 0) {
      setError(messages.join(' | '));
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredProducts = useMemo(() => {
    if (!search.trim()) return products;
    const query = search.toLowerCase();
    return products.filter(product => (product.name || '').toLowerCase().includes(query));
  }, [products, search]);

  const handleChange = event => {
    const { name, value, type } = event.target;
    setForm(current => ({ ...current, [name]: type === 'number' ? Number(value) : value }));
  };

  const startEdit = product => {
    setEditing(getProductId(product));
    setForm({
      name: product.name || '',
      category: product.category || 'pc',
      condition: product.condition || 'reconditionne',
      grade: product.grade || 'A',
      price: product.price ?? '',
      description: product.description || '',
      processor: product.processor || '',
      ram: product.ram || '',
      storage: product.storage || '',
      screen_size: product.screen_size || '',
      warranty: product.warranty || '6 mois',
      stock: product.stock ?? 1,
    });
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startCreate = () => {
    setEditing('new');
    setForm(EMPTY_PRODUCT);
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(EMPTY_PRODUCT);
    setError('');
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Le nom est obligatoire');
      return;
    }

    setSaving(true);
    setError('');

    const data = { ...form, price: parseFloat(form.price) || 0 };

    try {
      if (editing === 'new') {
        await apiFetchJson(PRODUCTS_ENDPOINT, {
          method: 'POST',
          body: JSON.stringify(data),
        }, 'Erreur de création');
      } else {
        await apiFetchJson(`/api/admin/products/${editing}?site=boutique`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }, 'Erreur de mise à jour');
      }

      cancelEdit();
      await fetchData();
    } catch (err) {
      setError(err.message || 'Erreur réseau');
    }

    setSaving(false);
  };

  const handleDelete = async productId => {
    if (!window.confirm('Supprimer ce produit ?')) return;

    try {
      await apiFetchJson(`/api/admin/products/${productId}?site=boutique`, {
        method: 'DELETE',
      }, 'Erreur de suppression');
      await fetchData();
    } catch (err) {
      setError(err.message || 'Erreur réseau');
    }
  };

  const handleToggleMaintenance = async () => {
    setMaintenanceSaving(true);
    setError('');

    try {
      const data = await apiFetchJson(
        MAINTENANCE_ENDPOINT,
        {
          method: 'PUT',
          body: JSON.stringify({ enabled: !maintenance.enabled }),
        },
        'Erreur de mise a jour du mode maintenance',
      );
      setMaintenance({ ...DEFAULT_MAINTENANCE, ...(data || {}) });
    } catch (err) {
      setError(err.message || 'Erreur reseau');
    }

    setMaintenanceSaving(false);
  };

  return (
    <MyLayout title="Boutique Atelier Météris">
      <div className="space-y-6">
        <div className="bg-[#12141a] border border-white/5 rounded-2xl p-4">
          <p className="text-gray-400 text-sm">
            Les données boutique sont chargées via le proxy sécurisé du portail. Aucune reconnexion secondaire n'est requise.
          </p>
        </div>

        <div className={`border rounded-2xl p-5 ${
          maintenance.enabled
            ? 'bg-amber-500/10 border-amber-500/20'
            : 'bg-[#12141a] border-white/5'
        }`}>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  maintenance.enabled ? 'bg-amber-500/15 text-amber-300' : 'bg-white/5 text-gray-400'
                }`}>
                  <Wrench className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-white font-semibold">Mode maintenance boutique</p>
                  <p className="text-xs text-gray-400">
                    Le site public affiche un bandeau discret et refuse toute nouvelle reservation.
                  </p>
                </div>
              </div>
              <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${
                maintenance.enabled
                  ? 'bg-amber-500/15 text-amber-300'
                  : 'bg-emerald-500/10 text-emerald-400'
              }`}>
                <span className={`w-2 h-2 rounded-full ${maintenance.enabled ? 'bg-amber-400' : 'bg-emerald-400'}`} />
                {maintenance.enabled ? 'Maintenance active' : 'Reservations ouvertes'}
              </div>
              {maintenance.enabled && (
                <p className="text-xs text-amber-200/80">{maintenance.message}</p>
              )}
            </div>

            <button
              onClick={handleToggleMaintenance}
              disabled={maintenanceSaving}
              className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition-colors disabled:opacity-50 ${
                maintenance.enabled
                  ? 'bg-white/10 text-white hover:bg-white/15'
                  : 'bg-amber-500 text-black hover:bg-amber-400'
              }`}
            >
              {maintenanceSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
              {maintenance.enabled ? 'Desactiver la maintenance' : 'Activer la maintenance'}
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-sm text-red-400">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {stats?.stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total', value: stats.stats.total_products, color: 'text-white' },
              { label: 'Disponibles', value: stats.stats.available, color: 'text-emerald-400' },
              { label: 'Réservés', value: stats.stats.reserved, color: 'text-amber-400' },
              { label: 'Vendus', value: stats.stats.sold, color: 'text-gray-400' },
            ].map(item => (
              <div key={item.label} className="bg-[#12141a] border border-white/5 rounded-xl p-4 text-center">
                <div className={`text-2xl font-bold ${item.color}`}>{item.value ?? 0}</div>
                <div className="text-xs text-gray-500 mt-1">{item.label}</div>
              </div>
            ))}
          </div>
        )}

        {!editing && (
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Rechercher un produit..."
                className="w-full bg-[#12141a] border border-white/5 focus:border-[#0099D6]/50 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-gray-600 outline-none transition-colors"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <button
              onClick={startCreate}
              className="inline-flex items-center gap-2 bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-2.5 px-5 rounded-xl transition-colors text-sm shrink-0"
            >
              <Plus className="w-4 h-4" />
              Nouveau produit
            </button>
          </div>
        )}

        {editing && (
          <div className="bg-[#12141a] border border-white/5 rounded-2xl p-6">
            <h3 className="text-white font-semibold mb-5">
              {editing === 'new' ? 'Nouveau produit' : 'Modifier le produit'}
            </h3>
            <div className="space-y-4">
              <FormField label="Nom du produit *" name="name" value={form.name} onChange={handleChange} />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Catégorie</label>
                  <select name="category" value={form.category} onChange={handleChange}
                    className="w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-xl p-2.5 text-white text-sm outline-none">
                    <option value="pc">PC</option>
                    <option value="accessoire">Accessoire</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">État</label>
                  <select name="condition" value={form.condition} onChange={handleChange}
                    className="w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-xl p-2.5 text-white text-sm outline-none">
                    <option value="reconditionne">Reconditionné</option>
                    <option value="neuf">Neuf</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Grade</label>
                  <select name="grade" value={form.grade} onChange={handleChange}
                    className="w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-xl p-2.5 text-white text-sm outline-none">
                    <option value="A">Grade A</option>
                    <option value="B">Grade B</option>
                    <option value="">-</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Prix (€) *</label>
                  <input type="number" name="price" value={form.price} onChange={handleChange} min="0" step="0.01"
                    className="w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-xl p-2.5 text-white text-sm outline-none" />
                </div>
                <FormField label="Garantie" name="warranty" value={form.warranty} onChange={handleChange} placeholder="ex: 12 mois" />
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Stock</label>
                  <input type="number" name="stock" value={form.stock} onChange={handleChange} min="0"
                    className="w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-xl p-2.5 text-white text-sm outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Processeur" name="processor" value={form.processor} onChange={handleChange} placeholder="ex: Intel Core i5-1145G7" />
                <FormField label="RAM" name="ram" value={form.ram} onChange={handleChange} placeholder="ex: 16 Go DDR4" />
                <FormField label="Stockage" name="storage" value={form.storage} onChange={handleChange} placeholder="ex: SSD 512 Go" />
                <FormField label="Écran" name="screen_size" value={form.screen_size} onChange={handleChange} placeholder="ex: 15.6 pouces Full HD" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Description</label>
                <textarea name="description" value={form.description} onChange={handleChange} rows={3}
                  className="w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-xl p-2.5 text-white placeholder-gray-600 outline-none resize-none text-sm" />
              </div>
            </div>
            <div className="flex items-center gap-3 mt-6">
              <button onClick={handleSave} disabled={saving}
                className="inline-flex items-center gap-2 bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-2.5 px-5 rounded-xl transition-colors text-sm disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Enregistrer
              </button>
              <button onClick={cancelEdit}
                className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-gray-300 font-medium py-2.5 px-5 rounded-xl transition-colors text-sm">
                <X className="w-4 h-4" />
                Annuler
              </button>
            </div>
          </div>
        )}

        {search && !editing && (
          <p className="text-xs text-gray-500">
            {filteredProducts.length} résultat{filteredProducts.length !== 1 ? 's' : ''} pour « {search} »
          </p>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-7 h-7 text-[#0099D6] animate-spin" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="bg-[#12141a] border border-white/5 rounded-2xl p-12 text-center">
            <Package className="w-10 h-10 text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">
              {search ? 'Aucun produit ne correspond à votre recherche.' : 'Aucun produit. Créez le premier !'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredProducts.map(product => {
              const productId = getProductId(product);
              const statusConfig = STATUS_CONFIG[product.status] || STATUS_CONFIG.available;
              return (
                <div
                  key={productId}
                  className={`bg-[#12141a] border rounded-xl px-5 py-4 flex items-center gap-4 transition-colors ${
                    editing === productId ? 'border-[#0099D6]/30' : 'border-white/5 hover:border-white/10'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-2 mb-1">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusConfig.cls}`}>{statusConfig.label}</span>
                      <span className="text-[10px] text-gray-600 uppercase tracking-wider">{product.category}</span>
                      {product.condition === 'reconditionne' && (
                        <span className="text-[10px] text-gray-600">· Reconditionné {product.grade && `Grade ${product.grade}`}</span>
                      )}
                    </div>
                    <h4 className="text-sm font-medium text-white truncate">{product.name}</h4>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-[#0099D6] text-sm font-semibold">
                        {typeof product.price === 'number' ? `${product.price.toFixed(2)} €` : product.price}
                      </span>
                      {product.processor && <span className="text-[10px] text-gray-600 truncate">{product.processor}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => startEdit(product)}
                      className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 transition-colors"
                      title="Modifier"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(productId)}
                      className="p-2 rounded-lg hover:bg-red-500/10 text-gray-600 hover:text-red-400 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!editing && (
          <div className="flex justify-center pt-2">
            <a
              href="https://boutique.meteris.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs text-gray-500 hover:text-gray-300 transition-colors"
            >
              <Tag className="w-3.5 h-3.5" />
              Voir la boutique publique
            </a>
          </div>
        )}
      </div>
    </MyLayout>
  );
}

function FormField({ label, name, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-400 mb-1.5">{label}</label>
      <input
        type="text"
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-xl p-2.5 text-white placeholder-gray-600 outline-none text-sm"
      />
    </div>
  );
}
